import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './ConsultaConsumoBar.css';

/**
 * Página para Admin consultar consumo BAR por rodada e marcar como pago
 */

const ConsultaConsumoBar = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadas, setRodadas] = useState([]);
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [consumos, setConsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarRodadas();
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (rodadaSelecionada) {
      carregarConsumos();
    }
  }, [rodadaSelecionada]);

  const carregarRodadas = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      // Incluir rodadas abertas E fechadas para permitir visualizar saldo durante a rodada
      const todasRodadas = Array.isArray(rodadasRes.data) 
        ? rodadasRes.data.filter(r => r.status === 'aberta' || r.status === 'fechada')
        : [];

      // Ordenar: rodadas abertas primeiro, depois fechadas (mais recentes primeiro)
      todasRodadas.sort((a, b) => {
        if (a.status === 'aberta' && b.status === 'fechada') return -1;
        if (a.status === 'fechada' && b.status === 'aberta') return 1;
        return new Date(b.data) - new Date(a.data);
      });

      setRodadas(todasRodadas);

      if (todasRodadas.length > 0 && !rodadaSelecionada) {
        setRodadaSelecionada(todasRodadas[0].id);
      }

      setError('');
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
      setError(err.response?.data?.error || 'Erro ao carregar rodadas');
    } finally {
      setLoading(false);
    }
  };

  const carregarConsumos = async () => {
    if (!rodadaSelecionada) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/consumos-bar/rodada/${rodadaSelecionada}`);
      setConsumos(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar consumos:', err);
      setError(err.response?.data?.error || 'Erro ao carregar consumos');
      setConsumos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarComoPago = async (jogadorId) => {
    if (!window.confirm('Deseja marcar o saldo BAR deste jogador como pago?')) {
      return;
    }

    try {
      setSalvando(true);
      await apiClient.put(`/consumos-bar/rodada/${rodadaSelecionada}/marcar-pago`, {
        jogador_id: jogadorId,
      });

      alert('Saldo BAR marcado como pago com sucesso!');
      carregarConsumos();
    } catch (err) {
      console.error('Erro ao marcar como pago:', err);
      setError(err.response?.data?.error || 'Erro ao marcar como pago');
    } finally {
      setSalvando(false);
    }
  };

  if (loading && !rodadas.length) {
    return <div className="container">Carregando...</div>;
  }

  const rodadaSelecionadaObj = rodadas.find(r => r.id === rodadaSelecionada);
  const totalArrecadado = consumos.reduce((sum, item) => sum + parseFloat(item.saldo_bar || 0), 0);
  const totalPago = consumos.filter(item => item.saldo_bar_pago).reduce((sum, item) => sum + parseFloat(item.saldo_bar || 0), 0);
  const totalPendente = totalArrecadado - totalPago;

  return (
    <div className="container consulta-consumo-bar-container">
      <div className="page-header">
        <h1>🍺 Consulta Consumo BAR</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="form-group rodada-selector">
        <label>Selecione a Rodada:</label>
        <select
          value={rodadaSelecionada}
          onChange={(e) => setRodadaSelecionada(e.target.value)}
          className="select-rodada"
        >
          {rodadas.length > 0 ? (
            rodadas.map(rodada => (
              <option key={rodada.id} value={rodada.id}>
                {new Date(rodada.data).toLocaleDateString('pt-BR')} - {rodada.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
              </option>
            ))
          ) : (
            <option value="">Nenhuma rodada disponível</option>
          )}
        </select>
      </div>

      {rodadaSelecionadaObj && (
        <>
          {rodadaSelecionadaObj.status === 'aberta' && (
            <div className="info-box" style={{ 
              background: '#e7f3ff', 
              border: '1px solid #2196F3', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '20px' 
            }}>
              <p style={{ margin: 0, color: '#1976D2' }}>
                <strong>ℹ️ Rodada Aberta:</strong> O saldo pode ser atualizado conforme novos consumos forem registrados.
              </p>
            </div>
          )}
          <div className="resumo-section">
            <div className="resumo-card">
              <h3>Total Arrecadado BAR</h3>
              <p className="valor">R$ {totalArrecadado.toFixed(2)}</p>
            </div>
            <div className="resumo-card">
              <h3>Total Pago</h3>
              <p className="valor positivo">R$ {totalPago.toFixed(2)}</p>
            </div>
            <div className="resumo-card">
              <h3>Total Pendente</h3>
              <p className="valor negativo">R$ {totalPendente.toFixed(2)}</p>
            </div>
          </div>

          <div className="consumos-section">
            <h2>Detalhamento por Jogador</h2>
            {consumos.length > 0 ? (
              <table className="consumos-table">
                <thead>
                  <tr>
                    <th>Jogador</th>
                    <th>Saldo BAR</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consumos.map((item, index) => (
                    <tr key={item.jogador?.id || index}>
                      <td>{item.jogador?.nome || 'Jogador'}</td>
                      <td className="saldo-bar">
                        R$ {parseFloat(item.saldo_bar || 0).toFixed(2)}
                      </td>
                      <td>
                        {(() => {
                          const saldo = parseFloat(item.saldo_bar || 0);
                          if (saldo === 0) {
                            // Saldo zero: não mostrar status
                            return null;
                          } else if (item.saldo_bar_pago) {
                            // Saldo > 0 e pago: mostrar Pago
                            return <span className="status-pago">✅ Pago</span>;
                          } else {
                            // Saldo > 0 e não pago: mostrar Pendente
                            return <span className="status-pendente">⏳ Pendente</span>;
                          }
                        })()}
                      </td>
                      <td>
                        {!item.saldo_bar_pago && parseFloat(item.saldo_bar || 0) > 0 && (
                          <button
                            onClick={() => handleMarcarComoPago(item.jogador?.id)}
                            disabled={salvando}
                            className="btn btn-success btn-sm"
                          >
                            Marcar como Pago
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="sem-consumos">Nenhum consumo registrado para esta rodada.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConsultaConsumoBar;
